import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { ACCENT, BRAND, ICON, RADIUS, SYSTEM, TEXT } from '../../theme/tokens'

/** True for a file this page can read as CSV (by name, or by type when unnamed). */
export const isCsvFile = (file) =>
    !!file &&
    (/\.csv$/i.test(file.name ?? '') ||
        (!file.name && /csv/i.test(file.type ?? '')))

/**
 * Where the recipients file goes: click, keyboard (Enter or Space) or drag
 * and drop all hand the file to the same `onFile`, and Clear removes the
 * loaded file. Only .csv files are taken.
 */
const FileDropZone = ({ fileName, onFile, onClear }) => {
    const inputRef = useRef(null)
    const [dragging, setDragging] = useState(false)
    const [refused, setRefused] = useState('')

    const take = (file) => {
        if (!file) return
        if (!isCsvFile(file)) {
            setRefused(`${file.name || 'That file'} is not a .csv file.`)
            return
        }
        setRefused('')
        onFile(file)
    }

    const open = () => inputRef.current?.click()

    return (
        <Box sx={{ mb: 3 }}>
            <Box
                // A real control: a clickable div a keyboard cannot reach was
                // replaced by this.
                role="button"
                tabIndex={0}
                aria-label={
                    fileName
                        ? `Change CSV file (now ${fileName})`
                        : 'Upload a CSV file'
                }
                data-testid="csv-drop-zone"
                onClick={open}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        open()
                    }
                }}
                onDragOver={(e) => {
                    e.preventDefault()
                    if (!dragging) setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                    e.preventDefault()
                    setDragging(false)
                    take(e.dataTransfer?.files?.[0])
                }}
                sx={{
                    border: `2px dashed ${
                        dragging ? BRAND.orange : ACCENT.border55
                    }`,
                    borderRadius: `${RADIUS.md}px`,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    // While a file is dragged over: the accent tint (its text pair is
                    // in theme/contrast.test.js).
                    background: dragging ? ACCENT.bg12 : ACCENT.bg04,
                    transition: 'all 0.2s',
                    '&:hover, &:focus-visible': {
                        borderColor: BRAND.orange,
                        background: ACCENT.bg08,
                    },
                }}
            >
                <CloudUploadIcon
                    sx={{
                        fontSize: ICON.hero,
                        // On the orange tint: ACCENT.strong keeps 3:1.
                        color: ACCENT.strong,
                        mb: 1,
                    }}
                />
                <Typography variant="body2" sx={{ color: TEXT.faint }}>
                    {dragging
                        ? 'Drop the CSV file here'
                        : fileName || 'Click or drop a CSV file here'}
                </Typography>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".csv"
                    hidden
                    data-testid="csv-file-input"
                    onChange={(e) => {
                        take(e.target.files?.[0])
                        // Lets the same file be picked again after Clear.
                        e.target.value = ''
                    }}
                />
            </Box>
            {refused && (
                <Typography
                    variant="body2"
                    role="alert"
                    sx={{ color: SYSTEM.errorSoft, mt: 1 }}
                >
                    {refused}
                </Typography>
            )}
            {fileName && (
                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setRefused('')
                            onClear()
                        }}
                    >
                        Clear file
                    </Button>
                </Box>
            )}
        </Box>
    )
}

FileDropZone.propTypes = {
    fileName: PropTypes.string,
    onFile: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
}

export default FileDropZone
